import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  color: string;
}

export default function Services() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number>(0);

  // --- CONFIGURACIÓN DE SERVICIOS CON LINKS ---
  const services = [
    { 
      icon: 'ri-building-line', 
      title: 'Urbanix', 
      description: 'Plataforma integral para la administración inteligente de propiedad horizontal. Gestiona apartamentos, áreas comunes, pagos y residentes de forma automatizada.',
      keywords: 'software administración propiedad horizontal, sistema gestión condominos, app administrador edificios',
      status: 'active',
      link: 'https://urbanix.online/' 
    },
    { 
      icon: 'ri-parking-box-line', 
      title: 'Parqium', 
      description: 'Sistema moderno para la gestión automatizada de parqueaderos y accesos. Control de estacionamiento, reservas y pagos automáticos.',
      keywords: 'software gestión parqueaderos, sistema control estacionamiento, app reserva parking',
      status: 'active',
      link: 'https://parqium.com/' 
    },
    { 
      icon: 'ri-money-dollar-circle-line', 
      title: 'PrestControl', 
      description: 'Solución financiera para el control total de préstamos y clientes. Gestión de cartera, cobros y reportes financieros.',
      keywords: 'software gestión préstamos, sistema control crediticio, app gestión financiera clientes',
      status: 'active',
      link: 'https://prestcontrol.netlify.app/' 
    },
    { 
      icon: 'ri-line-chart-line', 
      title: 'TrackCloud', 
      description: 'Herramienta para la gestión inteligente de finanzas con reportes visuales. Dashboard financiero y control de gastos empresariales.',
      keywords: 'software finanzas empresas, dashboard financiero SaaS, gestión gastos empresariales',
      status: 'active',
      link: 'https://track-cloud.vercel.app/' 
    },
    { 
      icon: 'ri-gift-line', 
      title: 'Jackpot', 
      description: 'Sistema para la gestión segura y transparente de sorteos automatizados. Sorteos justos con verificación blockchain.',
      keywords: 'software sorteos automatizados, plataforma rifas online, sorteos transparentes',
      status: 'coming-soon',
      link: null 
    },
    { 
      icon: 'ri-restaurant-line', 
      title: 'FoodScan', 
      description: 'Menús digitales interactivos y experiencias sin contacto para restaurantes. Códigos QR con menús automatizados.',
      keywords: 'menú digital restaurante, código QR menú, app pedidos restaurante',
      status: 'coming-soon',
      link: null 
    }
  ];

  // --- MOTOR DE PARTÍCULAS (Sin cambios) ---
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = canvas.parentElement?.offsetHeight || 900;
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    const colors = ['#00C2FF', '#1A73E8', '#ffffff'];
    particlesRef.current = Array.from({ length: 30 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      radius: Math.random() * 1.5 + 0.5,
      color: colors[Math.floor(Math.random() * colors.length)]
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particlesRef.current.forEach((p, i) => {
        p.x = (p.x + p.vx + canvas.width) % canvas.width;
        p.y = (p.y + p.vy + canvas.height) % canvas.height;
        
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = 0.15; 
        ctx.fill();

        particlesRef.current.slice(i + 1).forEach(p2 => {
          const dist = Math.hypot(p.x - p2.x, p.y - p2.y);
          if (dist < 180) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = p.color;
            ctx.globalAlpha = (1 - dist / 180) * 0.05;
            ctx.stroke();
          }
        });
      });
      animationRef.current = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationRef.current);
    };
  }, []);

  // Función para manejar el clic en servicios sin link
  const handleComingSoon = () => {
    alert("Estamos trabajando en ello. ¡Próximamente más!");
  };

  return (
    <section id="services" className="py-16 sm:py-24 lg:py-32 bg-[#050608] relative">
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none z-0" />
      <div className="absolute top-0 left-0 w-full h-40 bg-gradient-to-b from-[#050608] via-[#050608]/80 to-transparent z-10 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative z-20">
        <div className="text-center mb-14 sm:mb-20 lg:mb-24">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/5 border border-white/10 rounded-full backdrop-blur-md mb-6 sm:mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00C2FF] shadow-[0_0_8px_#00C2FF]"></span>
            <span className="text-white text-[11px] font-bold uppercase tracking-[0.2em]">Productos CodeLiance</span>
          </div>
          <h2 className="text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-5 sm:mb-8 tracking-tight">
            Ecosistema <span className="bg-gradient-to-r from-[#00C2FF] to-[#1A73E8] bg-clip-text text-transparent">Digital</span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed font-medium px-2">
            Potenciamos tu negocio con herramientas de alta precisión y diseño vanguardista.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-8">
          {services.map((service, index) => (
            <div 
              key={index}
              className="group relative p-1 rounded-[2rem] sm:rounded-[2.5rem] bg-gradient-to-b from-white/15 to-transparent transition-all duration-500 hover:scale-[1.02]"
            >
              <div className="h-full w-full bg-[#0d0f14]/90 backdrop-blur-sm rounded-[2rem] sm:rounded-[2.4rem] p-6 sm:p-8 lg:p-10 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#00C2FF]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                <div className="relative z-10 flex flex-col h-full">
                  <div className="w-12 h-12 sm:w-14 md:w-16 sm:h-14 md:h-16 flex items-center justify-center rounded-xl sm:rounded-2xl bg-white/5 border border-white/10 mb-5 sm:mb-8 group-hover:bg-[#00C2FF]/20 group-hover:border-[#00C2FF]/30 transition-all duration-500">
                    <i className={`${service.icon} text-2xl sm:text-3xl text-white group-hover:text-[#00C2FF] transition-colors`}></i>
                  </div>

                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">{service.title}</h3>
                  <p className="text-gray-400 text-sm sm:text-[15px] leading-relaxed mb-6 sm:mb-8 flex-grow">{service.description}</p>

                  {/* LÓGICA DE BOTÓN DINÁMICO */}
                  {service.link ? (
                    <a 
                      href={service.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-[#00C2FF] font-bold text-xs uppercase tracking-widest pt-4 border-t border-white/5 group-hover:border-[#00C2FF]/20 transition-colors cursor-pointer"
                    >
                      <span>Ver Detalles</span>
                      <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                    </a>
                  ) : (
                    <div 
                      onClick={handleComingSoon}
                      className="flex items-center gap-2 text-gray-500 font-bold text-xs uppercase tracking-widest pt-4 border-t border-white/5 cursor-help"
                    >
                      <span>Próximamente</span>
                      <i className="ri-time-line"></i>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-[#050608] to-transparent z-10 pointer-events-none" />
    </section>
  );
}